import { ALLOWED_UPLOAD_EXTENSIONS, MAX_FILESIZE } from "@nnr/global";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const ALLOWED_FILE_TYPE_EXTENSIONS = ALLOWED_UPLOAD_EXTENSIONS;

interface UseFileCollectionOptions {
  multiple?: boolean;
  accept?: string;
  maxFiles?: number | null;
  maxFileSize?: number | null; // in bytes
  onSelect?: (files: File[]) => void;
}

interface FileInfo {
  count: number;
  totalSize: number;
  types: string[];
}

interface FileCollection {
  files: File[];
  onSelectHandler: () => void;
  addFiles: (newFiles: File | File[]) => void;
  removeFile: (index: number) => void;
  removeFileByRef: (fileToRemove: File) => void;
  clearFiles: () => void;
  getFileInfo: () => FileInfo;
}

export const useFileCollection = (
  options: UseFileCollectionOptions = {},
): FileCollection => {
  const {
    multiple = true,
    accept = ALLOWED_FILE_TYPE_EXTENSIONS.join(","),
    maxFiles = null,
    maxFileSize = MAX_FILESIZE,
    onSelect,
  } = options;

  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Create and manage the hidden file input
  const createFileInput = useCallback((): HTMLInputElement => {
    if (!fileInputRef.current) {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = multiple;
      input.accept = accept;
      input.style.display = "none";
      document.body.appendChild(input);
      fileInputRef.current = input;
    }
    return fileInputRef.current;
  }, [multiple, accept]);

  const onSelectHandler = useCallback((): void => {
    const input = createFileInput();

    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const selectedFiles = Array.from(target.files || []);

      // Filter files based on constraints
      let validFiles = selectedFiles;

      if (maxFileSize) {
        validFiles = validFiles.filter((file) => file.size <= maxFileSize);
        const rejectedCount = selectedFiles.length - validFiles.length;
        if (rejectedCount > 0) {
          console.warn(`${rejectedCount} file(s) rejected due to size limit`);
          const toastLabel = new Intl.PluralRules("en-US").select(
            rejectedCount,
          );
          toast.error(
            `${rejectedCount} file${toastLabel === "one" ? "" : "s"} exceed the maximum size of ${
              maxFileSize / (1024 * 1024)
            } MB and ${toastLabel === "one" ? "was" : "were"} not added.`,
          );
        }
      }

      setFiles((prevFiles) => {
        const newFiles = multiple ? [...prevFiles, ...validFiles] : validFiles;

        // Apply max files limit
        if (maxFiles && newFiles.length > maxFiles) {
          console.warn(
            `Only ${maxFiles} files allowed. Additional files ignored.`,
          );
          return newFiles.slice(0, maxFiles);
        }

        return newFiles;
      });

      // Reset input value to allow selecting the same file again
      input.value = "";
    };

    input.click();
  }, [createFileInput, maxFiles, maxFileSize, multiple]);

  const addFiles = useCallback(
    (newFiles: File | File[]): void => {
      const filesToAdd = Array.isArray(newFiles) ? newFiles : [newFiles];

      let validFiles = filesToAdd.filter((file) => file instanceof File);

      if (maxFileSize) {
        validFiles = validFiles.filter((file) => file.size <= maxFileSize);
      }

      setFiles((prevFiles) => {
        const combined = multiple ? [...prevFiles, ...validFiles] : validFiles;

        if (maxFiles && combined.length > maxFiles) {
          return combined.slice(0, maxFiles);
        }

        return combined;
      });
    },
    [maxFiles, maxFileSize, multiple],
  );

  const removeFile = useCallback((index: number): void => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  }, []);

  const removeFileByRef = useCallback((fileToRemove: File): void => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  }, []);

  const clearFiles = useCallback((): void => {
    setFiles([]);
  }, []);

  const getFileInfo = useCallback((): FileInfo => {
    return {
      count: files.length,
      totalSize: files.reduce((total, file) => total + file.size, 0),
      types: [...new Set(files.map((file) => file.type))],
    };
  }, [files]);

  const cleanup = useCallback((): void => {
    if (fileInputRef.current) {
      document.body.removeChild(fileInputRef.current);
      fileInputRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (onSelect && files.length > 0) {
      onSelect(files);
    }
  }, [files]);

  return {
    files,
    onSelectHandler,
    addFiles,
    removeFile,
    removeFileByRef,
    clearFiles,
    getFileInfo,
  };
};
